import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { FillingBatch, FillingBatchesState } from '../../types/fillingBatch';
import { fillingBatchApiService } from '../../services/fillingBatchApiService';
import { fillingBatchIngredientApiService } from '../../services/fillingBatchIngredientApiService';
import { fillingBatchComponentBatchApiService } from '../../services/fillingBatchComponentBatchApiService';
import { FillingIngredient, FillingComponentBatch as FillingComponentBatchLocal } from '../../types/fillingRecord';
import { RootState } from '../store';

// Helper function to create a blank filling batch
export const createBlankFillingBatch = (): FillingBatch => {
  const today = new Date().toISOString().split('T')[0];
  return {
    id: `temp-${uuidv4()}`,  // Use temp- prefix so isTempRecord check works
    productName: '',
    productId: '',
    dateOfProduction: today,
    batchLotNumber: '',
    preparedBy: '',
    verifiedBy: '',
    timesProduced: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

// Async thunks

/**
 * Fetch all filling batches from the API
 */
export const fetchFillingBatches = createAsyncThunk(
  'fillingBatches/fetchFillingBatches',
  async () => {
    return await fillingBatchApiService.fetchFillingBatches();
  }
);

interface CreateFillingBatchPayload {
  batch: FillingBatch;
  ingredients: FillingIngredient[];
  componentBatches: FillingComponentBatchLocal[];
}

/**
 * Create a new filling batch with its ingredients and component batches
 */
export const createFillingBatchAsync = createAsyncThunk(
  'fillingBatches/createFillingBatch',
  async (payload: CreateFillingBatchPayload, { getState }) => {
    const { batch, ingredients, componentBatches } = payload;

    console.log('[createFillingBatchAsync] Starting batch creation');
    console.log('[createFillingBatchAsync] Original batch ID (temp):', batch.id);
    console.log('[createFillingBatchAsync] Ingredients to save:', ingredients.length);
    console.log('[createFillingBatchAsync] Component batches to save:', componentBatches.length);

    // First, create the filling batch to get the real ID
    const createdBatch = await fillingBatchApiService.createFillingBatch(batch);
    const realBatchId = createdBatch.id;

    console.log('[createFillingBatchAsync] Batch created with real ID:', realBatchId);
    console.log('[createFillingBatchAsync] ID returned from API:', createdBatch.id);

    // Get raw ingredients from state for lookup
    const state = getState() as RootState;
    const allRawIngredients = state.rawIngredients.ingredients;

    // Save all ingredients with the real batch ID
    if (ingredients.length > 0) {
      console.log('[createFillingBatchAsync] Saving ingredients with fillingBatchId:', realBatchId);
      await Promise.all(
        ingredients.map(async (ing) => {
          // Find the rawIngredientId by ingredient name
          const rawIngredient = allRawIngredients.find(ri => ri.name === ing.ingredientName);
          if (!rawIngredient) {
            console.error(`Raw ingredient not found for name: ${ing.ingredientName}`);
            return;
          }

          console.log('[createFillingBatchAsync] Creating ingredient:', ing.ingredientName, 'with fillingBatchId:', realBatchId);
          return fillingBatchIngredientApiService.createFillingBatchIngredient({
            fillingBatchId: realBatchId,
            rawIngredientId: rawIngredient.id,
            lotNumbers: Array.isArray(ing.lotNumbers) ? ing.lotNumbers : [ing.lotNumbers],
            quantity: ing.quantity ?? null,
            unit: ing.unit ?? null
          });
        })
      );
      console.log('[createFillingBatchAsync] All ingredients saved successfully');
    }

    // Save all component batches with the real batch ID
    if (componentBatches.length > 0) {
      console.log('[createFillingBatchAsync] Saving component batches with fillingBatchId:', realBatchId);
      await Promise.all(
        componentBatches.map(async (cb) => {
          console.log('[createFillingBatchAsync] Creating component batch link:', cb.componentBatchId, 'with fillingBatchId:', realBatchId);
          return fillingBatchComponentBatchApiService.createFillingBatchComponentBatch({
            fillingBatchId: realBatchId,
            componentBatchId: cb.componentBatchId
          });
        })
      );
      console.log('[createFillingBatchAsync] All component batches saved successfully');
    }

    console.log('[createFillingBatchAsync] Batch creation complete, returning batch with ID:', createdBatch.id);
    return createdBatch;
  }
);

/**
 * Update an existing filling batch
 */
export const updateFillingBatchAsync = createAsyncThunk(
  'fillingBatches/updateFillingBatch',
  async (fillingBatch: FillingBatch) => {
    return await fillingBatchApiService.updateFillingBatch(fillingBatch);
  }
);

/**
 * Delete a filling batch
 */
export const deleteFillingBatchAsync = createAsyncThunk(
  'fillingBatches/deleteFillingBatch',
  async (id: string) => {
    await fillingBatchApiService.deleteFillingBatch(id);
    return id;
  }
);

/**
 * Fetch a single filling batch by ID
 */
export const fetchFillingBatchById = createAsyncThunk(
  'fillingBatches/fetchFillingBatchById',
  async (id: string) => {
    return await fillingBatchApiService.fetchFillingBatchById(id);
  }
);

// Initial state
const initialState: FillingBatchesState = {
  items: [],
  loading: false,
  error: null,
};

const fillingBatchSlice = createSlice({
  name: 'fillingBatches',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch filling batches
    builder
      .addCase(fetchFillingBatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFillingBatches.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchFillingBatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch filling batches';
      });

    // Create filling batch
    builder
      .addCase(createFillingBatchAsync.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(createFillingBatchAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create filling batch';
      });

    // Update filling batch
    builder
      .addCase(updateFillingBatchAsync.fulfilled, (state, action) => {
        const index = state.items.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateFillingBatchAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update filling batch';
      });

    // Delete filling batch
    builder
      .addCase(deleteFillingBatchAsync.fulfilled, (state, action) => {
        state.items = state.items.filter(b => b.id !== action.payload);
      })
      .addCase(deleteFillingBatchAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete filling batch';
      });

    // Fetch filling batch by ID
    builder
      .addCase(fetchFillingBatchById.fulfilled, (state, action) => {
        // Check if batch already exists to prevent duplicates
        const existingIndex = state.items.findIndex(b => b.id === action.payload.id);
        if (existingIndex === -1) {
          state.items.push(action.payload);
        } else {
          // Update existing batch if found
          state.items[existingIndex] = action.payload;
        }
      })
      .addCase(fetchFillingBatchById.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch filling batch by ID';
      });
  }
});

export default fillingBatchSlice.reducer;
